import React, { useState } from "react";
import axios from "axios";
import { Mail, MapPin, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Overline = ({ children }) => (
  <span className="font-mono-el text-xs tracking-[0.25em] uppercase text-[#4FC3F7]">{children}</span>
);

const ROLES = [
  "Data Engineer / Analyst",
  "Data Scientist",
  "Engineering Leader (VP / Director)",
  "Founder / C-Suite",
  "Product Manager",
  "Other",
];

const inputClass =
  "w-full bg-[#020817]/60 border border-white/10 focus:border-[#4FC3F7] focus:ring-1 focus:ring-[#4FC3F7] focus:outline-none text-white placeholder:text-[#475569] px-4 py-3 text-sm transition-colors";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    use_case: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company || !form.role) {
      toast.error("Please fill in your name, email, company, and role.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/demo-requests`, form);
      setDone(true);
      toast.success("Request received — we'll be in touch soon.");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative pt-40 pb-24 min-h-screen grid-bg">
        <div className="absolute top-32 right-0 w-[500px] h-[500px] rounded-full bg-[#1565C0]/15 blur-[120px]" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* LEFT */}
          <div className="lg:col-span-5">
            <Reveal>
              <Overline>Request a Demo</Overline>
              <h1 className="mt-6 text-4xl md:text-5xl font-light leading-tight tracking-tight text-white">
                Let's unify your data.
              </h1>
              <p className="mt-6 text-lg text-[#94A3B8] leading-relaxed">
                Tell us about your stack and what you're trying to build. We'll show you how Apeiron's
                agents handle the lifecycle — and explore whether you're a fit for our design-partner
                program.
              </p>

              <div className="mt-12 space-y-6">
                <a
                  href="mailto:info@etherlake.ai"
                  data-testid="contact-email-link"
                  className="flex items-center gap-4 text-[#94A3B8] hover:text-white transition-colors group"
                >
                  <span className="w-11 h-11 flex items-center justify-center border border-white/10 bg-white/5 text-[#4FC3F7] group-hover:bg-[#4FC3F7] group-hover:text-[#020817] transition-colors">
                    <Mail className="w-5 h-5" strokeWidth={1.5} />
                  </span>
                  info@etherlake.ai
                </a>
                <div className="flex items-center gap-4 text-[#94A3B8]">
                  <span className="w-11 h-11 flex items-center justify-center border border-white/10 bg-white/5 text-[#4FC3F7]">
                    <MapPin className="w-5 h-5" strokeWidth={1.5} />
                  </span>
                  Remote-first · Building globally
                </div>
              </div>
            </Reveal>
          </div>

          {/* RIGHT - FORM */}
          <div className="lg:col-span-7">
            <Reveal delay={120} className="bg-white/5 border border-white/10 p-8 md:p-12 backdrop-blur-md">
              {done ? (
                <div data-testid="contact-success" className="py-16 text-center">
                  <CheckCircle2 className="w-16 h-16 text-[#4FC3F7] mx-auto" strokeWidth={1.2} />
                  <h2 className="mt-6 text-2xl font-medium text-white">Request received.</h2>
                  <p className="mt-3 text-[#94A3B8] max-w-md mx-auto">
                    Thanks, {form.name.split(" ")[0]}. We've logged your request and will reach out at{" "}
                    <span className="text-white">{form.email}</span> shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm text-[#94A3B8] mb-2">Full name *</label>
                      <input
                        data-testid="contact-name-input"
                        type="text"
                        value={form.name}
                        onChange={update("name")}
                        placeholder="Jane Doe"
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-[#94A3B8] mb-2">Work email *</label>
                      <input
                        data-testid="contact-email-input"
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        placeholder="jane@company.com"
                        className={inputClass}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm text-[#94A3B8] mb-2">Company *</label>
                      <input
                        data-testid="contact-company-input"
                        type="text"
                        value={form.company}
                        onChange={update("company")}
                        placeholder="Acme Inc."
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-[#94A3B8] mb-2">Your role *</label>
                      <select
                        data-testid="contact-role-select"
                        value={form.role}
                        onChange={update("role")}
                        className={`${inputClass} appearance-none`}
                      >
                        <option value="" disabled className="bg-[#020817]">
                          Select a role
                        </option>
                        {ROLES.map((r) => (
                          <option key={r} value={r} className="bg-[#020817]">
                            {r}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-[#94A3B8] mb-2">
                      Primary use case <span className="text-[#475569]">(optional)</span>
                    </label>
                    <input
                      data-testid="contact-usecase-input"
                      type="text"
                      value={form.use_case}
                      onChange={update("use_case")}
                      placeholder="e.g. real-time analytics, demand forecasting, unified governance"
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-[#94A3B8] mb-2">
                      Tell us about your stack <span className="text-[#475569]">(optional)</span>
                    </label>
                    <textarea
                      data-testid="contact-message-input"
                      value={form.message}
                      onChange={update("message")}
                      rows={4}
                      placeholder="What are you using today, and what are you trying to solve?"
                      className={`${inputClass} resize-none`}
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={submitting}
                    className="group w-full inline-flex items-center justify-center gap-2 bg-[#4FC3F7] text-[#020817] hover:bg-[#00E5FF] disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300 px-8 py-4 font-semibold"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" /> Sending…
                      </>
                    ) : (
                      <>
                        Request a Demo
                        <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                      </>
                    )}
                  </button>
                  <p className="text-xs text-[#475569] text-center">
                    We'll only use your details to respond to your request.
                  </p>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
